import { Helmet } from "react-helmet-async";

const DEFAULT_URL = "https://prosecours.fr";
const DEFAULT_IMAGE = `${DEFAULT_URL}/og-image.jpg`;

export function SeoHome() {
  const title =
    "ProSecours — Formalités juridiques à Paris | Création & procédures collectives";
  const description =
    "ProSecours, société de formalités juridiques basée à Paris : création de société, modifications statutaires, dépôt de bilan, liquidation judiciaire, redressement judiciaire, sauvegarde, conciliation et mandat ad hoc. Premier échange gratuit.";
  const keywords =
    "formalités juridiques paris, société de formalités juridiques, création de société, modification statuts, dépôt de bilan, liquidation judiciaire paris, liquidation judiciaire, redressement judiciaire, sauvegarde entreprise, conciliation entreprise, mandat ad hoc, cessation de paiement, greffe, annonce légale";

  const org = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name: "ProSecours",
    description,
    url: DEFAULT_URL,
    telephone: "+33 7 44 88 33 81",
    areaServed: { "@type": "Country", name: "France" },
    address: { "@type": "PostalAddress", addressCountry: "FR", addressLocality: "Paris" },
    priceRange: "€€",
    knowsAbout: [
      "Formalités juridiques",
      "Création de société",
      "Modifications statutaires",
      "Dépôt de bilan",
      "Liquidation judiciaire",
      "Redressement judiciaire",
      "Sauvegarde",
      "Conciliation",
      "Mandat ad hoc",
      "Cessation de paiement",
      "Cession d'entreprise",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.7",
      reviewCount: "3",
    },
    review: [
      {
        "@type": "Review",
        author: { "@type": "Person", name: "Fulghesu" },
        datePublished: "2026-03-10",
        reviewRating: { "@type": "Rating", ratingValue: "5" },
        reviewBody:
          "Statuts rédigés, annonce légale publiée, immatriculation au greffe — je n'ai eu rien à gérer. Tout était clair dès le devis. Je recommande vivement.",
      },
      {
        "@type": "Review",
        author: { "@type": "Person", name: "Chantal Harribey" },
        datePublished: "2026-02-18",
        reviewRating: { "@type": "Rating", ratingValue: "5" },
        reviewBody:
          "Après deux exercices difficiles, la conciliation ouverte par ProSecours nous a permis de rééchelonner notre dette URSSAF. La banque est restée à nos côtés. Un vrai soulagement.",
      },
      {
        "@type": "Review",
        author: { "@type": "Person", name: "Anonyme" },
        datePublished: "2026-04-12",
        reviewRating: { "@type": "Rating", ratingValue: "4" },
        reviewBody:
          "Modification de statuts et transmission de parts gérées avec une précision remarquable. Chaque étape expliquée simplement, sans jargon. On se sent accompagné du début à la fin.",
      },
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={DEFAULT_URL + "/"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={DEFAULT_URL + "/"} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <script type="application/ld+json">{JSON.stringify(org)}</script>
    </Helmet>
  );
}

export function SeoJournal() {
  const title = "Journal — Formalités juridiques & procédures | ProSecours";
  const description =
    "Publications régulières sur les formalités juridiques et les procédures collectives : cessation de paiement, sauvegarde, redressement, création de société. Ressources pour dirigeants.";
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={DEFAULT_URL + "/journal"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={DEFAULT_URL + "/journal"} />
      <meta property="og:locale" content="fr_FR" />
    </Helmet>
  );
}

export function SeoArticle({ article }) {
  const url = `${DEFAULT_URL}/journal/${article.slug}`;
  const title = article.seoTitle || `${article.title} — ProSecours`;
  const description = article.seoDescription || article.excerpt;

  const ld = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description,
    datePublished: article.date,
    author: { "@type": "Organization", name: "ProSecours" },
    publisher: {
      "@type": "Organization",
      name: "ProSecours",
      url: DEFAULT_URL,
    },
    mainEntityOfPage: url,
    keywords: article.keywords,
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {article.keywords && <meta name="keywords" content={article.keywords} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}
